import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { fetchDadosBasicosFromS3 } from '../../lib/s3Theme'

import { selectMinData } from '../../redux/selectors/dadosBasicos'
import { selectSearchOptions } from '../../redux/selectors/searchOptions'

import '../../assets/sass/cards.scss'
import {
  formatDocument,
  isPending,
  useJuridicoNotification,
  useTokenValidation
} from '../../utils'

import {
  Content,
  JiveWindow,
  GoogleMapsImages,
  GoogleMapsResponsive
} from '../../components'

import {
  RamoDeAtividade,
  DadosBasicosCNPJ,
  DadosBasicosCPF,
  Beneficiarios,
  Socios as SociosComponent,
  Funcionarios as FuncionariosComponent,
  Empresas as EmpresasComponent,
  PessoasFisicasRelacionadas as PessoasFisicasRelacionadasComponent,
  Contatos as ContatosComponent,
  HistoricoFuncional as HistoricoFuncionalComponent,
  Parentes as ParentesComponent
} from './components'
import {
  JURIDICO_NOTIFICATION_INTERVAL,
  TOKEN_VALIDATION_INTERVAL
} from '../../config/consts'

const DadosBasicos = () => {
  useTokenValidation(TOKEN_VALIDATION_INTERVAL)
  useJuridicoNotification(JURIDICO_NOTIFICATION_INTERVAL)
  const googleMaps = useSelector(state => state.googleMaps)
  const { pages: enabledPages } = useSelector(selectSearchOptions)
  const { isCPF, targetName, targetDocument } = useSelector(selectMinData)

  const dadosBasicosTheme = useSelector(state => state.dadosBasicosTheme)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState({
    dadosBasicos: { data: [], loaded: false, error: false }
  })

  useEffect(() => {
    if (!dadosBasicosTheme.loaded) {
      return
    }

    setLoading(true)
    fetchDadosBasicosFromS3({
      payload: dadosBasicosTheme
    })
      .then(dadosBasicos => {
        setData(prev => ({ ...prev, dadosBasicos }))
      })
      .catch(error => {
        toast.error(error.message)
        console.error(error)
        setData(prev => ({
          ...prev,
          dadosBasicos: { data: [], loaded: false, error: true }
        }))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dadosBasicosTheme, isCPF])

  const isStepPending = targetDocument && isPending(dadosBasicosTheme)

  const renderPJ = () => {
    return (
      <>
        <JiveWindow
          title='Ramo de Atividade'
          name='dadosBasicos.ramoDeAtividade'
          showNote
        >
          <RamoDeAtividade
            error={data?.dadosBasicos.error}
            data={data?.dadosBasicos.data?.dadosBasicos?.ramoDeAtividade}
          />
        </JiveWindow>
        <JiveWindow
          title='Beneficiários'
          subtitle='Dados retirados da Neoway'
          name='dadosBasicos.beneficiarios'
          showNote
        >
          <Beneficiarios
            data={data.dadosBasicos.data.beneficiarios}
            error={data?.dadosBasicos.error}
          />
        </JiveWindow>
        <Funcionarios isCPF={isCPF} dadosBasicos={data.dadosBasicos} />
        <Socios isCPF={isCPF} dadosBasicos={data?.dadosBasicos} />
      </>
    )
  }

  const renderPF = () => {
    return (
      <>
        <HistoricoFuncional isCPF={isCPF} dadosBasicos={data?.dadosBasicos} />
        <Parentes isCPF={isCPF} dadosBasicos={data?.dadosBasicos} />
      </>
    )
  }

  return (
    <Content
      title={`Dados Básicos ${targetName ? `- ${targetName}` : ''}`}
      subtitle='Dados principais retirados da receita federal'
    >
      {!enabledPages.dadosBasicos ? (
        <div className='d-flex w-100 h-100 flex-1 justify-content-center align-items-center'>
          <p className='h3'>Esta aba está desabilitada!</p>
        </div>
      ) : loading || isStepPending ? (
        <div className='d-flex w-100 h-100 flex-1 justify-content-center align-items-center'>
          <div
            className='spinner-border text-primary'
            style={{ width: '3rem', height: '3rem' }}
            role='status'
          >
            <span className='sr-only'>Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <JiveWindow
            title={targetDocument ? targetName : 'Dados Básicos'}
            subtitle={`${
              targetDocument ? (isCPF ? 'CPF:' : 'CNPJ:') : ''
            } ${formatDocument(targetDocument)}`}
            name='dadosBasicos.dadosBasicos'
            showNote
          >
            {isCPF ? (
              <DadosBasicosCPF dadosCadastrais={data?.dadosBasicos} />
            ) : (
              <DadosBasicosCNPJ dadosCadastrais={data?.dadosBasicos} />
            )}
          </JiveWindow>
          <Contatos isCPF={isCPF} dadosBasicos={data?.dadosBasicos} />
          {isCPF ? renderPF() : renderPJ()}
          <PessoasFisicasRelacionadas dadosBasicos={data.dadosBasicos} />
          <Empresas isCPF={isCPF} dadosBasicos={data.dadosBasicos} />
          {googleMaps.isLoadedImages && <GoogleMaps googleMaps={googleMaps} />}
        </>
      )}
    </Content>
  )
}

const Funcionarios = React.memo(({ isCPF, dadosBasicos }) => {
  if (isCPF) {
    return null
  }

  return (
    <JiveWindow
      title='Funcionários'
      subtitle='Dados retirados da Neoway'
      name='dadosBasicos.funcionarios'
      showNote
    >
      <FuncionariosComponent
        data={dadosBasicos.data?.funcionarios}
        error={dadosBasicos.error}
      />
    </JiveWindow>
  )
})

const Socios = React.memo(({ isCPF, dadosBasicos }) => {
  if (isCPF) {
    return null
  }

  return (
    <JiveWindow
      title='Sócios'
      subtitle='Dados retirados da Receita Federal e Neoway'
      name='dadosBasicos.socios'
      showNote
    >
      <SociosComponent data={dadosBasicos.data} error={dadosBasicos.error} />
    </JiveWindow>
  )
})

const Empresas = React.memo(({ isCPF, dadosBasicos }) => {
  const [selectedTable, setSelectedTable] = useState(undefined)

  const tableDataMap = {
    coligadas: 'Dados processados da Receita Federal',
    correlacionadas: 'Dados retirados da PH3a',
    participacoes: 'Dados retirados da Receita Federal',
    familiar: 'Dados retirados da Receita Federal',
    socios: 'Dados retirados da Receita Federal'
  }

  return (
    <JiveWindow
      title='Empresas'
      subtitle={
        selectedTable
          ? tableDataMap[selectedTable]
          : 'Dados retirados da Receita Federal, PH3a e Neoway'
      }
      name='dadosBasicos.empresas'
      showNote
    >
      <EmpresasComponent
        isCPF={isCPF}
        dadosBasicos={dadosBasicos}
        onChange={setSelectedTable}
      />
    </JiveWindow>
  )
})

const PessoasFisicasRelacionadas = React.memo(({ dadosBasicos }) => {
  return (
    <JiveWindow
      title='Pessoas físicas relacionadas'
      subtitle='Dados retirados da PH3A'
      name='dadosBasicos.pessoasFisicasRelacionadas'
      showNote
    >
      <PessoasFisicasRelacionadasComponent
        data={dadosBasicos.data}
        error={dadosBasicos.error}
      />
    </JiveWindow>
  )
})

const Parentes = React.memo(({ isCPF, dadosBasicos }) => {
  const parentes = dadosBasicos.data?.pessoasFisicasRelacionadas?.parentes

  if (!isCPF) {
    return null
  }

  return (
    <JiveWindow
      title='Parentes'
      subtitle='Dados retirados do BigDataCorp e PH3a. Esse campo é aproximado por uso de inteligência artificial.'
      name='dadosBasicos.parentes'
      showNote
    >
      <ParentesComponent data={parentes} error={dadosBasicos.error} />
    </JiveWindow>
  )
})

const Contatos = React.memo(({ isCPF, dadosBasicos }) => {
  if (!dadosBasicos?.loaded) {
    return null
  }

  return (
    <JiveWindow
      title='Contatos'
      name='dadosBasicos.telefones'
      subtitle='Dados retirados da PH3a e Receita Federal.'
      showNote
    >
      <ContatosComponent
        isCPF={isCPF}
        data={dadosBasicos.data}
        error={dadosBasicos.error}
      />
    </JiveWindow>
  )
})

const HistoricoFuncional = React.memo(({ isCPF, dadosBasicos }) => {
  if (!isCPF) {
    return null
  }

  if (!dadosBasicos?.loaded) {
    return null
  }

  return (
    <JiveWindow
      title='Histórico funcional'
      name='dadosBasicos.historicoFuncional'
      subtitle='Dados retirados da Neoway.'
      showNote
    >
      <HistoricoFuncionalComponent
        data={dadosBasicos.data}
        error={dadosBasicos.error}
      />
    </JiveWindow>
  )
})

const GoogleMaps = React.memo(({ googleMaps }) => {
  return (
    <JiveWindow title='Google Maps' showNote name='dadosBasicos.googleMaps'>
      <div className='row'>
        <div className='col-12 mb-4 d-flex justify-content-center'>
          <GoogleMapsImages data={googleMaps} />
        </div>
      </div>
      <div className='row'>
        <div className='col-12 d-flex justify-content-center'>
          <GoogleMapsResponsive
            query={googleMaps.address}
            height='600px'
            width='460px'
          />
        </div>
      </div>
    </JiveWindow>
  )
})

export default DadosBasicos
